import { showNotificationError } from '@utils/functions'
import axios from 'axios'

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * A request made in the background, which the user did not ask for (a re-check, a
     * reconciliation): when it fails, no error notification is shown. The caller still gets the
     * rejection and decides what, if anything, to say.
     */
    silent?: boolean
  }
}

/** The config of the request an axios error belongs to (a network error has no response). */
const requestConfigOf = (error: any) => error?.config ?? error?.response?.config

/**
 * What every failed request of the app's axios instances goes through (see baseService): it
 * shows the error notification, then passes the rejection on to the caller.
 */
export const errorResponseInterceptor = (data: any) => {
  // A request its caller aborted is not a failure: no toast.
  if (axios.isCancel(data)) {
    return Promise.reject(data)
  }

  // A background request (config.silent): its caller handles the failure, nothing is shown.
  if (requestConfigOf(data)?.silent === true) {
    return Promise.reject(data)
  }

  const status = data.response?.status
  const config = data.response?.config
  if (
    (status === 401 || status == 500) &&
    config.url === 'app/account/my-profile'
  ) {
    return Promise.reject(data)
  }

  // Token refresh failures (refresh_token expired / invalid) have their own
  // UX path — handleRefreshToken surfaces the "Session Expired" modal. Skip
  // the generic error toast so the user only sees one clear message.
  if (config?.url === '/connect/token') {
    return Promise.reject(data)
  }

  if (status === 408) {
    const errorMessage =
      'This function is taking longer than expected to complete. Please try again in a few minutes.'
    showNotificationError(errorMessage)
    return Promise.reject(errorMessage)
  }

  if (status === 409) {
    const errorMessage =
      "The data you're working on has been modified by other user. Please refresh to get latest data and try again."
    showNotificationError(errorMessage)
    return Promise.reject(errorMessage)
  }

  if (status !== 500) {
    showNotificationError(data)
  }

  return Promise.reject(data)
}
